import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  SpaceBetween,
  Box,
  Modal,
  Wizard,
} from '@cloudscape-design/components';

function DemoTutorial({ onDismiss }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [tutorialVisible, setTutorialVisible] = useState(true);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  // Reset tutorial step when route changes
  useEffect(() => {
    const pathToStepMap = {
      '/': 0,
      '/upload': 1,
      '/manual-analysis': 2,
      '/analysis': 3,
    };
    setActiveStepIndex(pathToStepMap[location.pathname] || 0);
  }, [location.pathname]);

  const handleDismiss = () => {
    setTutorialVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  const tutorialSteps = [
    {
      title: 'Welcome to ResearchIQ',
      description: 'Introduction to the application',
      content: (
        <Box padding="l">
          <SpaceBetween size="m">
            <div>
              Welcome to the ResearchIQ, our Financial Research Assistant demo. This tutorial will guide you through the application's features.
            </div>
            <div>
              DocuMind AI helps you analyze financial documents, extract key insights, and generate comprehensive reports.
            </div>
            <div>
              <Button onClick={() => navigate('/upload')}>Start Demo</Button>
            </div>
          </SpaceBetween>
        </Box>
      )
    },
    {
      title: 'Start Document Analysis',
      description: 'Begin the AI-powered analysis',
      content: (
        <Box padding="l">
          <SpaceBetween size="m">
            <div>
              Click "Start AI Analysis" to begin the automated document analysis process.
            </div>
            <div>
              The AI will use advanced natural language processing to extract key information, 
              analyze financial metrics, and identify important insights from the document.
            </div>
            <div>
              You'll see real-time progress as the AI processes the document and generates comprehensive analysis.
            </div>
          </SpaceBetween>
        </Box>
      )
    },
    {
      title: 'Review AI Analysis Response',
      description: 'Examine the AI-generated insights',
      content: (
        <Box padding="l">
          <SpaceBetween size="m">
            <div>
              Review the comprehensive analysis results generated by the AI system.
            </div>
            <div>
              The AI provides detailed answers to specific questions about the financial document, 
              including citations and references to source material.
            </div>
            <div>
              Compare the speed and accuracy of AI analysis with traditional manual methods.
            </div>
            <div>
              Notice how the AI can quickly identify and extract complex financial information 
              that might take hours to find manually.
            </div>
          </SpaceBetween>
        </Box>
      )
    },
    {
      title: 'Review Additional AI Insights',
      description: 'Explore deeper analysis and recommendations',
      content: (
        <Box padding="l">
          <SpaceBetween size="m">
            <div>
              Explore additional insights and recommendations provided by the AI analysis.
            </div>
            <div>
              The AI can identify patterns, trends, and relationships in the financial data 
              that may not be immediately obvious from manual review.
            </div>
            <div>
              Review performance metrics, risk assessments, and comparative analysis 
              that help inform investment decisions.
            </div>
            <div>
              This demonstrates how AI can augment human expertise by providing comprehensive, 
              data-driven insights for financial research and analysis.
            </div>
          </SpaceBetween>
        </Box>
      )
    }
  ];

  // Custom rendering for the wizard footer to ensure the Finish button is properly displayed
  const renderFooter = () => {
    const isLastStep = activeStepIndex === tutorialSteps.length - 1;
    
    return (
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={handleDismiss}>
            Skip tutorial
          </Button>
          {activeStepIndex > 0 && (
            <Button onClick={() => setActiveStepIndex(activeStepIndex - 1)}>
              Previous
            </Button>
          )}
          <Button variant="primary" onClick={() => {
            if (isLastStep) {
              handleDismiss();
            } else {
              setActiveStepIndex(activeStepIndex + 1);
            }
          }}>
            {isLastStep ? 'Finish' : 'Next'}
          </Button>
        </SpaceBetween>
      </Box>
    );
  };

  return (
    <Modal
      visible={tutorialVisible}
      onDismiss={handleDismiss}
      size="large"
      header="DocuMind AI Tutorial"
      footer={renderFooter()}
    >
      <Wizard
        steps={tutorialSteps}
        activeStepIndex={activeStepIndex}
        onNavigate={({ detail }) => setActiveStepIndex(detail.requestedStepIndex)}
        i18nStrings={{
          stepNumberLabel: stepNumber => `Step ${stepNumber}`,
          collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
        }}
        hideNavigationButtons={true} // Hide the default navigation buttons
      />
    </Modal>
  );
}

export default DemoTutorial;
